import { Box, DatePickerInput, RadioGroup } from '@sequencehq/core-components'
import { format } from '@sequencehq/utils/dates'
import { FC } from 'react'
import { getScheduleChangeDateBounds, ScheduleDates } from '@sequencehq/utils'
import {
  DATE_FORMAT,
  IMMEDIATE_DISABLED_REASON,
  NEXT_BILLING_DATE_UNKNOWN_REASON,
  SCHEDULE_ENDS_FIRST_REASON
} from './scheduleChangeDateSelector.constants'

export type ScheduleChangeDateType =
  | 'immediate'
  | 'nextBillingDate'
  | 'customDate'

type Props = {
  name: string
  value: ScheduleChangeDateType | undefined
  onChange: (value: ScheduleChangeDateType) => void
  customDate: Date | undefined
  onCustomDateChange: (date: Date | undefined) => void
  scheduleDates: ScheduleDates
  isDisabled?: boolean
  disabledReason?: string
}

/**
 * Picks the date a change to a schedule takes effect. Shared by the upgrade
 * shortcut and the quote amendment modal, so the bounds only have to be worked
 * out once - pass it the schedule's dates and it takes care of the rest.
 */
export const ScheduleChangeDateSelector: FC<Props> = ({
  name,
  value,
  onChange,
  customDate,
  onCustomDateChange,
  scheduleDates,
  isDisabled,
  disabledReason
}) => {
  const bounds = getScheduleChangeDateBounds(scheduleDates)

  const isImmediateDisabled = isDisabled || bounds.isFutureStartingSchedule
  const isNextBillingDateDisabled =
    isDisabled ||
    !scheduleDates.nextBillingDate ||
    bounds.scheduleEndsBeforeNextBillingDate

  const nextBillingDateDisabledReason = bounds.scheduleEndsBeforeNextBillingDate
    ? SCHEDULE_ENDS_FIRST_REASON
    : NEXT_BILLING_DATE_UNKNOWN_REASON

  // Disabling the whole group says why on every option, so it wins over the
  // reason an individual option would have given.
  const disabledReasonFor = (isOptionDisabled: boolean, reason: string) => {
    if (!isOptionDisabled) {
      return undefined
    }

    return isDisabled ? disabledReason : reason
  }

  return (
    <RadioGroup
      name={name}
      onChange={newValue => onChange(newValue as ScheduleChangeDateType)}
      value={value ?? ''}
      options={[
        {
          label: 'Immediately',
          description: 'Changes take effect immediately.',
          labelAnnotation:
            value === 'immediate' ? format(new Date(), DATE_FORMAT) : '',
          value: 'immediate',
          disabled: isImmediateDisabled,
          disabledReason: disabledReasonFor(
            isImmediateDisabled,
            IMMEDIATE_DISABLED_REASON
          )
        },
        {
          label: 'Next billing period',
          description:
            'Changes take effect at the start of the next billing period.',
          labelAnnotation:
            value === 'nextBillingDate' && scheduleDates.nextBillingDate
              ? format(scheduleDates.nextBillingDate, DATE_FORMAT)
              : '',
          value: 'nextBillingDate',
          disabled: isNextBillingDateDisabled,
          disabledReason: disabledReasonFor(
            isNextBillingDateDisabled,
            nextBillingDateDisabledReason
          )
        },
        {
          label: 'Custom date',
          description: 'Changes take effect on a custom date.',
          value: 'customDate',
          disabled: isDisabled,
          disabledReason: isDisabled ? disabledReason : undefined,
          additionalComponent:
            value === 'customDate' && !isDisabled ? (
              // The calendar is portalled, but a click inside it still bubbles
              // through React to the option row, which would re-pick the option
              // and throw the date away again.
              <Box paddingTop="12px" onClick={event => event.stopPropagation()}>
                <DatePickerInput
                  minDate={bounds.minimumCustomDate}
                  maxDate={bounds.maximumCustomDate}
                  width="180px"
                  value={customDate}
                  onChange={onCustomDateChange}
                />
              </Box>
            ) : null
        }
      ]}
    />
  )
}
